import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import RemoveIcon from "@mui/icons-material/PersonRemoveOutlined"
import LeaveIcon from "@mui/icons-material/LogoutOutlined"

import { chatApi, userApi } from "../../api"
import { useAuth } from "../../context/AuthContext"
import { avatarColor, initialsOf } from "../../utils/format"
import { rise } from "../../motion"

/**
 * Who is in this conversation, and who may change that.
 *
 * ── Why a direct message has no settings ────────────────────────────────────
 *
 * A DM is two people by definition. Adding a third is not "managing a DM", it is
 * starting a different conversation — and silently converting one into a group
 * would mean two people's private exchange gaining an audience with no moment
 * where anybody agreed to it. The server refuses it; this screen does not offer
 * it.
 *
 * ── Why leaving is separated from removing ──────────────────────────────────
 *
 * They are the same endpoint and completely different acts. Removing somebody
 * needs the permission to manage the room; leaving needs nothing but being in
 * it, and nobody should be trapped in a conversation because they cannot
 * administer it. The button for it is apart from the list, so it cannot be hit
 * while aiming at the row above.
 */
export default function ConversationSettings({ open, room, participants, onClose, onChanged }) {
  const { user } = useAuth()
  const [directory, setDirectory] = useState([])
  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirmLeave, setConfirmLeave] = useState(false)

  const manageable = room && room.kind !== "dm" && (room.my_role === "owner" || room.my_role === "admin")

  useEffect(() => {
    if (!open) return
    setPicked([])
    setError(null)
    setConfirmLeave(false)
    userApi
      .directory()
      .then((data) => setDirectory(data.users || []))
      .catch(() => setDirectory([]))
  }, [open])

  /* Only people who can actually be added: not already here, and from an
     organization that belongs in this room. Offering somebody the server will
     refuse is offering a choice that fails after they have made it. */
  const addable = useMemo(() => {
    const present = new Set(participants.map((p) => Number(p.user_id)))
    return directory.filter(
      (u) =>
        !present.has(Number(u.id)) &&
        (String(u.organization_id) === String(room?.client_id) || String(u.organization_id) === "0"),
    )
  }, [directory, participants, room])

  const run = useCallback(
    async (work, failure) => {
      setBusy(true)
      setError(null)
      try {
        await work()
        onChanged?.()
      } catch (err) {
        setError(err.userMessage || failure)
      } finally {
        setBusy(false)
      }
    },
    [onChanged],
  )

  const add = () =>
    run(async () => {
      await chatApi.addParticipants(room.id, picked.map((p) => p.id))
      setPicked([])
    }, "Could not add them")

  const remove = (participant) =>
    run(() => chatApi.removeParticipant(room.id, participant.user_id), "Could not remove them")

  const leave = () =>
    run(async () => {
      await chatApi.removeParticipant(room.id, user.id)
      onClose()
    }, "Could not leave the conversation")

  if (!room) return null

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {room.kind === "dm" ? "Conversation" : room.topic || room.title || "Conversation"}
        <Typography variant="caption" color="text.secondary" display="block">
          {participants.length} participant{participants.length === 1 ? "" : "s"}
          {room.client_label && room.client_id !== "0" ? ` · ${room.client_label}` : ""}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {room.kind === "dm" ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            A direct conversation is between two people. To include somebody else, start a topic thread — this one stays
            as it is.
          </Alert>
        ) : manageable ? (
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Autocomplete
              multiple
              size="small"
              sx={{ flex: 1 }}
              options={addable}
              value={picked}
              onChange={(_e, value) => setPicked(value)}
              getOptionLabel={(option) => option.name || option.email}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add people"
                  placeholder={addable.length === 0 ? "Everybody eligible is already here" : "Search by name"}
                />
              )}
            />
            <Button variant="contained" onClick={add} disabled={busy || picked.length === 0} sx={{ mt: "22px" }}>
              Add
            </Button>
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can see who is here, but only an owner or an admin of this conversation can change it.
          </Alert>
        )}

        <Stack spacing={0.5}>
          {participants.map((participant, index) => {
            const label = participant.name || participant.email || "Unknown"
            const isYou = Number(participant.user_id) === Number(user?.id)

            return (
              <Stack
                key={participant.user_id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 0.75, px: 1, borderRadius: 1.5, "&:hover": { bgcolor: "action.hover" }, ...rise(index) }}
              >
                <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: avatarColor(label) }}>
                  {initialsOf(label)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {label}
                    {isYou ? " (you)" : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {participant.email}
                  </Typography>
                </Box>
                {participant.role && participant.role !== "member" ? (
                  <Chip size="small" label={participant.role} sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }} />
                ) : null}
                {/* Never on yourself: leaving is the button below, and a remove
                    control on your own row is one mis-click from an exit nobody
                    confirmed. */}
                {manageable && !isYou ? (
                  <Tooltip title={`Remove ${label}`}>
                    <IconButton size="small" onClick={() => remove(participant)} disabled={busy} aria-label={`Remove ${label}`}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
            )
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        {room.kind !== "dm" ? (
          confirmLeave ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                You will stop receiving this conversation.
              </Typography>
              <Button size="small" color="error" onClick={leave} disabled={busy}>
                Leave
              </Button>
              <Button size="small" onClick={() => setConfirmLeave(false)}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Button size="small" color="inherit" startIcon={<LeaveIcon />} onClick={() => setConfirmLeave(true)} disabled={busy}>
              Leave conversation
            </Button>
          )
        ) : (
          <Box />
        )}
        <Button onClick={onClose} disabled={busy}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
